import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Modal, ModalProps } from 'antd';
import React, { useEffect, useState } from 'react';
import InfiniteTable from '../InfiniteTable';
import { ColumnsType, SorterResult } from 'antd/es/table/interface';
import { COLUMNS_WIDTH, tableSorterOrder } from '@/utils/table';
import { useIntl } from 'react-intl';
import StatusTag from '../StatusTag';
import moment from 'moment';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import EditIcon from '../EditIcon';
import {
  AccountPayableColumnConfig,
  AccountPayableFieldOption,
  AccountPayableOrderColumn,
  AccountPayableSituationColor,
  InstallmentList,
  InstallmentSituation,
  InstallmentSituationType,
  ListInstallmentsQuery,
} from '@/core/domain/financial/AccountPayable/AccountPayable.types';
import { useInstallmentsList } from '@/core/domain/financial/AccountPayable/AccountPayable.hooks';
import AppPageHeader from '../AppPageHeader';
import { useDebounce } from '@/hooks';
import { quantityFormatter } from '@/utils/number';

interface Props extends ModalProps {
  open: boolean;
  onCancel: VoidFunction;
  queryFilter?: ListInstallmentsQuery;
}

const AccountPayableListModal: React.FC<Props> = ({
  open,
  onCancel,
  queryFilter,
  ...props
}) => {
  const intl = useIntl();
  const [queryParams, setQueryParams] = useState<ListInstallmentsQuery>({
    ...queryFilter,
    order: 'DESC',
    orderColumns: AccountPayableOrderColumn.code,
  });

  useEffect(() => {
    if (queryFilter) {
      setQueryParams((prev) => ({ ...prev, ...queryFilter }));
    }
  }, [open]);

  const queryDebounce = useDebounce(queryParams, 500);
  const { data, isLoading, fetchNextPage, hasNextPage } = useInstallmentsList(
    queryDebounce,
    open,
  );
  const accountPayableItems = flatInfiniteData(data);

  const columns: ColumnsType<InstallmentList> = [
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.code',
      }),
      sorter: true,
      key: AccountPayableFieldOption.code,
    },
    {
      width: 220,
      dataIndex: 'companyName',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.companies',
      }),
      sorter: true,
      key: AccountPayableFieldOption.company,
      ellipsis: true,
    },
    {
      dataIndex: 'creditor',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.creditor',
      }),
      width: 220,
      key: AccountPayableFieldOption.creditor,
      sorter: true,
      ellipsis: true,
    },
    {
      width: 100,
      dataIndex: 'dueDate',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.dueDate',
      }),
      render: (value) => (value ? moment(value).format('DD/MM/YYYY') : '-'),
      sorter: true,
      key: AccountPayableFieldOption.dueDate,
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'situation',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.situation',
      }),
      render: (situation: InstallmentSituation) => (
        <StatusTag
          label={InstallmentSituationType[situation]}
          color={AccountPayableSituationColor[situation]}
        />
      ),
    },
    {
      dataIndex: 'value',
      title: intl.formatMessage({
        id: 'releaseBillToPay.list.header.value',
      }),
      render: (_, record) =>
        `R$ ${quantityFormatter(
          record.installmentValue +
            (record.discountIncrease ?? 0) -
            (record.credit ?? 0),
        )}`,
      width: COLUMNS_WIDTH.price,
      key: AccountPayableFieldOption.value,
      sorter: true,
    },
    {
      title: intl.formatMessage({
        id: 'releaseBillToReceive.list.header.actions',
      }),
      width: 50,
      align: 'right',
      fixed: 'right',
      render: (_, item) => (
        <EditIcon
          iconProps={{
            onClick: () =>
              window.open(
                `/app/financial/billToPay/releaseBillToPay/detail/accountPayableId/${item.accountPayableId}/installmentPayableId/${item.id}`,
                '_blank',
              ),
          }}
          disabled={
            !hasPermissions([Authorities.FINANCIAL_ACCOUNT_PAYABLE_EDIT])
          }
        />
      ),
    },
  ];

  function handleOrderAccountPayable(sorter: SorterResult<InstallmentList>) {
    if (sorter && sorter?.columnKey) {
      setQueryParams((prev) => {
        if (sorter && sorter?.columnKey) {
          const order = sorter.order
            ? tableSorterOrder[sorter.order]
            : undefined;

          if (
            prev.orderColumns === AccountPayableColumnConfig[sorter.columnKey]
          ) {
            return {
              ...prev,
              order,
            };
          }

          return {
            ...prev,
            order: tableSorterOrder.ascend,
            orderColumns: AccountPayableColumnConfig[sorter.columnKey],
          };
        }

        return prev;
      });
    }
  }

  const handleCancel = () => {
    setQueryParams({
      order: 'DESC',
      orderColumns: AccountPayableOrderColumn.code,
    });

    onCancel();
  };

  return (
    <Modal
      {...props}
      title="Lançamentos do Contas a Pagar"
      width={1000}
      open={open}
      onCancel={handleCancel}
      footer={false}
    >
      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
      />

      <InfiniteTable<InstallmentList>
        columns={columns}
        dataSource={accountPayableItems}
        loading={isLoading}
        rowKey={(row) => row.id}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            handleOrderAccountPayable(sorter);
          }
        }}
      />
    </Modal>
  );
};

export default AccountPayableListModal;
